/**
 * Shared state between the chat's browser cards and the rail that plays them:
 * which sessions exist, which one is focused, and the slot its player moves
 * into. Module state because the cards and the rail are separate trees.
 */

import type { TBrowserPreviewStatus } from './types';

interface IBrowserPanelState {
  /** Sessions whose chat card is mounted, oldest first. */
  sessionIds: readonly string[];
  focusedSessionId: string | null;
  /** Whether the rail's Browser block is expanded. */
  isOpen: boolean;
  /** At least one rail is mounted and wide enough to hold the player. */
  hasHost: boolean;
  /** The node inside the expanded Browser block that the player moves into. */
  slot: HTMLElement | null;
  /** Bumped by an explicit focus, so a collapsed rail can expand itself. */
  focusRequest: number;
  /** One-shot: an Open the Browser block hasn't scrolled into view yet. */
  scrollRequested: boolean;
  /** Live/Replay per session, for the badge next to its title in the rail. */
  sessionStatuses: Readonly<Record<string, TBrowserPreviewStatus>>;
}

interface ISessionRecord {
  startedAtMs?: number;
  order: number;
}

interface IRegisterOptions {
  startedAtMs?: number;
  /** Take focus when this is the newest session. */
  autoFocus?: boolean;
}

const INITIAL_STATE: IBrowserPanelState = {
  sessionIds: [],
  focusedSessionId: null,
  isOpen: false,
  hasHost: false,
  slot: null,
  focusRequest: 0,
  scrollRequested: false,
  sessionStatuses: {},
};

// Records outlive their card, so a remounted card neither re-takes focus nor
// loses its place in the ordering.
const records = new Map<string, ISessionRecord>();
const mountCounts = new Map<string, number>();
const listeners = new Set<() => void>();
const expandHandlers = new Map<string, () => void>();
let registrationOrder = 0;
// Set when the user collapses the focused session, so a card remounting
// doesn't quietly take the empty focus and reopen the player.
let focusCleared = false;
let hostCount = 0;
// A release's focus handoff, undone if the same session re-registers in the
// same task (a layout effect re-running, or StrictMode's double mount).
let handoff: { from: string; to: string | null } | null = null;
let state: IBrowserPanelState = INITIAL_STATE;

const notify = (): void => {
  for (const listener of listeners) listener();
};

const update = (next: Partial<IBrowserPanelState>): void => {
  state = { ...state, ...next };
  notify();
};

/**
 * Compare on rain's clock (`startedAtMs`) or registration order, never across
 * them: an order number always loses to an epoch millisecond.
 */
const isNewer = (
  candidate: ISessionRecord,
  current: ISessionRecord | undefined,
): boolean => {
  if (!current) return true;
  if (
    candidate.startedAtMs !== undefined &&
    current.startedAtMs !== undefined
  ) {
    return candidate.startedAtMs > current.startedAtMs;
  }
  return candidate.order > current.order;
};

const byAge = (a: string, b: string): number => {
  const recordA = records.get(a);
  const recordB = records.get(b);
  if (!recordA || !recordB) return 0;
  if (isNewer(recordA, recordB)) return 1;
  if (isNewer(recordB, recordA)) return -1;
  return 0;
};

/**
 * Register a mounted chat card's session. Returns the release function, which
 * drops the session from the rail's list once no card shows it.
 */
export function registerBrowserPanelSession(
  sessionId: string,
  { startedAtMs, autoFocus = false }: IRegisterOptions = {},
): () => void {
  const existing = records.get(sessionId);
  const record = existing ?? { startedAtMs, order: ++registrationOrder };
  records.set(sessionId, record);

  const count = mountCounts.get(sessionId) ?? 0;
  mountCounts.set(sessionId, count + 1);

  const next: Partial<IBrowserPanelState> = {};
  if (count === 0) {
    next.sessionIds = [...state.sessionIds, sessionId].sort(byAge);
  }
  const reclaimsFocus =
    handoff?.from === sessionId && handoff.to === state.focusedSessionId;
  if (handoff?.from === sessionId) handoff = null;
  // A focused session whose card is gone must not outrank ones on screen.
  const focused =
    state.focusedSessionId && mountCounts.has(state.focusedSessionId)
      ? records.get(state.focusedSessionId)
      : undefined;
  if (reclaimsFocus) {
    next.focusedSessionId = sessionId;
  } else if (autoFocus && !existing && isNewer(record, focused)) {
    focusCleared = false;
    next.focusedSessionId = sessionId;
    next.isOpen = true;
  } else if (!focused && !focusCleared) {
    // Without a focus the block shows no player; taking it doesn't open it.
    next.focusedSessionId = sessionId;
  }
  if (Object.keys(next).length > 0) update(next);

  let released = false;
  return () => {
    // Guarded: StrictMode may run a cleanup twice.
    if (released) return;
    released = true;
    const remaining = (mountCounts.get(sessionId) ?? 1) - 1;
    if (remaining > 0) {
      mountCounts.set(sessionId, remaining);
      return;
    }
    mountCounts.delete(sessionId);
    const sessionIds = state.sessionIds.filter(id => id !== sessionId);
    // Hand the focus to the newest session still on screen.
    const focusedSessionId =
      state.focusedSessionId === sessionId
        ? (sessionIds.at(-1) ?? null)
        : state.focusedSessionId;
    if (state.focusedSessionId === sessionId) {
      const pending = { from: sessionId, to: focusedSessionId };
      handoff = pending;
      queueMicrotask(() => {
        if (handoff === pending) handoff = null;
      });
    }
    const { [sessionId]: _released, ...sessionStatuses } =
      state.sessionStatuses;
    update({ sessionIds, focusedSessionId, sessionStatuses });
  };
}

/** Focus `sessionId`, expand the Browser block and bring it into view. */
export function focusBrowserPanelSession(sessionId: string): void {
  focusCleared = false;
  update({
    focusedSessionId: sessionId,
    isOpen: true,
    focusRequest: state.focusRequest + 1,
    // Unset without a host, or the next block to mount would scroll.
    scrollRequested: state.hasHost,
  });
}

/** Collapse the focused session so no player is shown until one is picked. */
export function clearBrowserPanelFocus(): void {
  focusCleared = true;
  if (state.focusedSessionId !== null) update({ focusedSessionId: null });
}

/**
 * Register how `sessionId`'s chat card opens its expanded view, so the rail
 * can offer the same action. Returns the release function.
 */
export function registerBrowserPanelExpand(
  sessionId: string,
  expand: () => void,
): () => void {
  expandHandlers.set(sessionId, expand);
  return () => {
    if (expandHandlers.get(sessionId) === expand) {
      expandHandlers.delete(sessionId);
    }
  };
}

/** Open `sessionId`'s expanded view, if its card is mounted. */
export function expandBrowserPanelSession(sessionId: string): void {
  expandHandlers.get(sessionId)?.();
}

/** Record `sessionId`'s preview status for the rail's title badge. */
export function setBrowserPanelSessionStatus(
  sessionId: string,
  status: TBrowserPreviewStatus,
): void {
  if ((state.sessionStatuses[sessionId] ?? null) === status) return;
  update({
    sessionStatuses: { ...state.sessionStatuses, [sessionId]: status },
  });
}

/** Mark the pending scroll as done; called by the Browser block. */
export function consumeBrowserPanelScroll(): void {
  if (state.scrollRequested) update({ scrollRequested: false });
}

/** Expand or collapse the Browser block without changing the focus. */
export function setBrowserPanelOpen(isOpen: boolean): void {
  update({ isOpen });
}

/**
 * Register a rail that can hold the player. Returns the release function;
 * ref-counted so a remounting rail never drops a host another one still holds.
 */
export function registerBrowserPanelHost(): () => void {
  hostCount += 1;
  if (!state.hasHost) update({ hasHost: true });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    hostCount -= 1;
    if (hostCount === 0) update({ hasHost: false });
  };
}

/** Callback ref for the node inside the expanded Browser block. */
export function setBrowserPanelSlot(slot: HTMLElement | null): void {
  if (state.slot !== slot) update({ slot });
}

export function getBrowserPanelState(): IBrowserPanelState {
  return state;
}

export function subscribeToBrowserPanel(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test seam — this module owns process-lifetime UI state. */
export function resetBrowserPanelRegistry(): void {
  records.clear();
  mountCounts.clear();
  expandHandlers.clear();
  registrationOrder = 0;
  focusCleared = false;
  hostCount = 0;
  handoff = null;
  state = INITIAL_STATE;
  notify();
}
